import { CHIP_LABELS, type Squad } from '@fantasy/types';
import { isChipInPlay } from '../lib/chipInPlay.ts';
import { Term } from './Term.tsx';
import { useGameweek } from '../lib/useGameweek.ts';

interface ChipStatusProps {
  squad: Squad;
}

/**
 * Where a squad's chip state actually shows up on screen. T-070/T-080 built
 * `activeChip`, `chipsUsed` and `chipsAvailable` into the Squad object and
 * nothing rendered any of them — a manager who played Bench Boost had no way
 * to tell the app knew. Reads `chipsUsed`/`chipsAvailable` straight off
 * `squad`; the "is it still in play" call goes through `isChipInPlay` (T-119)
 * rather than trusting `activeChip` alone, since FPL keeps reporting it for
 * days after the gameweek it was played in finishes.
 *
 * Not `ChipCalendar`'s job restated: that plans a *future* gameweek to spend
 * a chip in, and says nothing about what's in play now or already gone. This
 * answers "where do I stand", not "what should I do next".
 */
export function ChipStatus({ squad }: ChipStatusProps) {
  const { activeChip, chipsUsed, chipsAvailable, gameweek } = squad;
  const meta = useGameweek();

  // `chipsUsed` doesn't cover every squad shape that sets `activeChip` — an
  // imported or public squad can have one without the other (see this
  // ticket's notes). `squad.gameweek` is the gameweek `activeChip` itself
  // was parsed from, so it's a defensible fallback played-gameweek rather
  // than losing the chip entirely to a missing history.
  const activeChipGameweek = chipsUsed?.find((use) => use.chip === activeChip)?.gameweek ?? gameweek;
  const chipInPlay =
    activeChip !== undefined &&
    isChipInPlay({
      playedGameweek: activeChipGameweek,
      currentGameweek: meta?.currentGameweek,
      phase: meta?.phase,
      knownCurrentGameweek: gameweek,
    });

  return (
    <section className="panel chip-status" aria-labelledby="chip-status-heading">
      <h3 id="chip-status-heading">Chips</h3>

      {/* The chip in play changes what the gameweek means, so it gets its
       * own full-bleed banner rather than sitting as a pill among the used/
       * available lists below — see this ticket's own note in styles.css.
       * A chip played in a gameweek that has since finished is not
       * re-rendered here at all — `chipsUsed` below already names the
       * gameweek it was played in, in the past tense a finished chip needs. */}
      {chipInPlay && (
        <div className="chip-status-active" role="status">
          <span className="chip-status-active-label">Chip in play this gameweek</span>
          <strong className="chip-status-active-chip">{CHIP_LABELS[activeChip!]}</strong>
        </div>
      )}

      <div className="chip-status-lists">
        <div>
          <h4>Used</h4>
          {chipsUsed === undefined ? (
            <p className="hint">Not known — link your FPL team to see it.</p>
          ) : chipsUsed.length === 0 ? (
            <p className="hint">None yet.</p>
          ) : (
            <ul className="list-reset">
              {chipsUsed.map((use) => (
                <li key={`${use.chip}-${use.gameweek}`}>
                  {CHIP_LABELS[use.chip]} — Gameweek {use.gameweek}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h4>In hand</h4>
          {chipsAvailable.length === 0 ? (
            <p className="hint">None left.</p>
          ) : (
            <ul className="list-reset">
              {chipsAvailable.map((chip) => (
                <li key={chip}>
                  <Term term={chip}>{CHIP_LABELS[chip]}</Term>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
